import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, timer, fromEvent, EMPTY, TimeoutError } from 'rxjs';
import { catchError, finalize, mergeMap, retryWhen, switchMap, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { MsalService } from '@azure/msal-angular';
import { LoadingKanbanService } from 'src/app/shared/service/utils/loadingKanban.service';
import { NotificationService } from 'src/app/shared/service/utils/notification.service';
import { LoaderService } from 'src/app/shared/service/utils/loader.service';
import { UserClaimsService } from 'src/app/shared/service/session/user-claims.service';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { isBlobStorageUrl } from 'src/app/shared/utils/url.utils';

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {

  /** Origen de la asignación. Fijo mientras solo exista este canal. */
  private static readonly ASSIGNED_TYPE = 'CALL_CENTER';

  /** Si `acquireTokenSilent()` se cuelga (red obsoleta tras reanudar de suspensión) el request queda atascado en 401 para siempre sin esto. */
  private static readonly SILENT_REFRESH_TIMEOUT_MS = 10000;

  private enQueued = 0;
  private static pendingRequests: (() => void)[] = [];
  private static isOnline = navigator.onLine;

  /** Evita logouts concurrentes: varios requests en vuelo pueden fallar casi a la vez, y `logoutRedirect()` lanza `interaction_in_progress` si se invoca dos veces mientras la primera sigue en curso. */
  private isLoggingOut = false;

  constructor(
    private readonly loadingKanbanService: LoadingKanbanService,
    private readonly authService: MsalService,
    private readonly notificationService: NotificationService,
    private readonly loaderService: LoaderService,
    private readonly userClaims: UserClaimsService,
  ) {
    // Internet vuelve → volver a ejecutar pendientes
    fromEvent(globalThis, 'online').subscribe(() => {
      HttpConfigInterceptor.isOnline = true;
      const pendings = [...HttpConfigInterceptor.pendingRequests];
      HttpConfigInterceptor.pendingRequests = [];
      pendings.forEach(retry => retry());
    });

    fromEvent(globalThis, 'offline').subscribe(() => {
      HttpConfigInterceptor.isOnline = false;
    });
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return new Observable(observer => {
      this.processRequest(req, next, observer);
    });
  }

  private processRequest(req: HttpRequest<any>, next: HttpHandler, observer: any) {
    const hideLoader = req.headers.get('x-hide-loader') === 'true';

    if (!hideLoader) {
      this.showLoader();
    }

    const subscriptionKey = environment.Apim.subscription_key;
    const account = this.authService.instance.getActiveAccount();
    const retried = { value: false };

    const attachHeaders = (token?: string) => {
      let headers = req.headers;

      // La URL firmada ya lleva su autenticación en el query string: cualquier header extra
      // hace que Azure Blob responda `InvalidAuthenticationInfo`.
      if (isBlobStorageUrl(req.url)) {
        return req.clone({ headers });
      }

      headers = headers.set('Ocp-Apim-Subscription-Key', subscriptionKey);
      if (token) headers = headers.set('Authorization', `Bearer ${token}`);

      return req.clone({ headers });
    };

    const firstReq = attachHeaders(account?.idToken);
    const executeRequest = () => {
      next
        .handle(firstReq)
        .pipe(
          retryWhen(errors =>
            errors.pipe(
              mergeMap((error, i) => {
                const isNetworkError =
                  error.status === 0 ||
                  error.message?.includes('Failed to fetch');

                // Retry por 429
                if (error?.status === 429 && i < 2) {
                  const wait = 1000 * (i + 1);
                  return timer(wait);
                }

                // Offline → esperar reconexión
                if (isNetworkError) {
                  if (!HttpConfigInterceptor.isOnline) {
                    return new Observable<void>(sub => {
                      HttpConfigInterceptor.pendingRequests.push(() => {
                        this.processRequest(req, next, observer);
                        sub.complete();
                      });
                    });
                  }

                  if (i < 2) {
                    const wait = (i + 1) * 2000;
                    return timer(wait);
                  }
                }

                return throwError(() => error);
              })
            )
          ),

          catchError((error: HttpErrorResponse) => {
            if (error.status === 401 && !retried.value && account) {
              retried.value = true;

              return this.authService
                .acquireTokenSilent({
                  scopes: ['openid', 'profile'],
                  authority: environment.b2cPolicies.authorities.signUpSignIn.authority,
                  account
                })
                .pipe(
                  timeout(HttpConfigInterceptor.SILENT_REFRESH_TIMEOUT_MS),
                  switchMap(res => {
                    return next.handle(attachHeaders(res.idToken));
                  }),
                  catchError(err2 => {
                    if (err2 instanceof InteractionRequiredAuthError || err2 instanceof TimeoutError) {
                      this.logout(); // sesión irrecuperable (o el refresh nunca respondió)
                      return EMPTY;
                    }

                    if (err2.status === 401 || err2.status === 403) {
                      this.logout();
                      return EMPTY;
                    }

                    return throwError(() => err2);
                  })
                );
            }

            return throwError(() => error);
          }),

          finalize(() => {
            if (!hideLoader) {
              this.hideLoader();
            }
          })
        )
        .subscribe({
          next: event => observer.next(event),
          error: err => observer.error(err),
          complete: () => observer.complete(),
        });
    };

    executeRequest();
  }

  private showLoader(): void {
    if (this.enQueued === 0) {
      this.loadingKanbanService.updateValue(true);
      this.loaderService.show();
    }

    this.enQueued++;
  }

  private hideLoader(): void {
    this.enQueued--;

    if (this.enQueued <= 0) {
      this.enQueued = 0;
      this.loadingKanbanService.updateValue(false);
      this.loaderService.hide();
    }
  }

  async logout() {
    if (this.isLoggingOut) return;
    this.isLoggingOut = true;

    localStorage.clear();
    sessionStorage.clear();

    this.loaderService.show('Cerrando sesión');

    try {
      await Promise.race([
        this.authService.logoutRedirect(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('timeout')), 3000)
        )
      ]);
      return;
    } catch (error) {
      console.error('Error during logout redirect:', error);
      this.notificationService.error('No se pudo cerrar sesión automáticamente. Por favor, cierra la ventana manualmente.');
    }

    this.loaderService.hide();

    const tenantLogout =
      `https://${environment.b2cPolicies.authorityDomain}/` +
      `oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(
        environment.postLogoutRedirectUri
      )}`;

    globalThis.location.href = tenantLogout;
  }
}
